"use client";

import Link from "next/link";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import TrustBadges from "@/components/home/TrustBadges";
import BrowseContent from "@/components/home/BrowseContent";
import WhyChooseConfy from "@/components/home/WhyChooseConfy";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useAuth } from "@/lib/context/AuthContext";
import { useToast } from "@/lib/context/ToastContext";
import AdditionalProfileModal from "@/components/modals/AdditionalProfileModal";
import SimpleAuthModal from "@/components/auth/SimpleAuthModal";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchWithAuthRetry } from '@/lib/utils/fetchWithAuthRetry';
import { clearAuthSession } from '@/lib/services/auth';

export default function Home() {
  const router = useRouter();
  const { user } = useAuth();
  const toast = useToast();
  const { t } = useLanguage();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [selectedCourseTitle, setSelectedCourseTitle] = useState<string | undefined>();
  const [selectedCourseType, setSelectedCourseType] = useState<'course' | 'event'>('course');

  const handleCourseRegister = async (courseId: string, courseTitle?: string, courseType?: 'course' | 'event') => {
    // Check if user is authenticated
    if (!user) {
      setSelectedCourseId(courseId);
      setSelectedCourseTitle(courseTitle);
      setSelectedCourseType(courseType || 'course');
      setShowAuthModal(true);
      return;
    }

    try {
      const profileResponse = await fetchWithAuthRetry(`/api/courses/${courseId}/profile-completion`);

      if (profileResponse.status === 401) {
        toast.error(t('authModal.authSessionRestoreFailed'), {
          duration: 15000,
          action: { label: t('authModal.authClearSessionAndRetry'), onClick: () => { clearAuthSession(); window.location.reload() } }
        });
        return;
      }

      const profileData = await profileResponse.json();

      if (!profileResponse.ok) {
        console.error('Profile check server error:', profileData.error);
        toast.error(t('course.serverErrorTryAgain'));
        return;
      }

      if (!profileData.isComplete) {
        // Show modal to complete profile
        setSelectedCourseId(courseId);
        setSelectedCourseTitle(courseTitle);
        setSelectedCourseType(courseType || 'course');
        setShowProfileModal(true);
      } else {
        // Profile is complete, navigate to course/event page
        const basePath = courseType === 'event' ? '/event' : '/courses';
        router.push(`${basePath}/${courseId}`);
      }
    } catch (error) {
      console.error('Profile check error:', error);
      toast.error(t('course.networkErrorCheckConnection'));
    }
  };

  const handleProfileComplete = () => {
    setShowProfileModal(false);
    if (selectedCourseId) {
      const basePath = selectedCourseType === 'event' ? '/event' : '/courses';
      router.push(`${basePath}/${selectedCourseId}`);
    }
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50/70 via-blue-50/30 to-white pb-8 sm:pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 lg:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 text-gray-900 leading-tight">
              <span className="inline-block">{t('home.hero.title')}</span>{' '}
              <span className="inline-block text-blue-600">
                {t('home.hero.titleHighlight')}
              </span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.hero.subtitle')}
            </p>

            {/* Trust Badges - Social Proof */}
            <TrustBadges />

            {/* CTA Button */}
            <div className="mt-8 sm:mt-10 flex flex-col items-center gap-4">
              <Link href="/browse">
                <Button size="lg" className="gap-2">
                  {t('home.browseCourses')}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <p className="text-sm text-gray-500">
                {t('home.hero.ctaSubtext')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Browse Content Section */}
      <BrowseContent onRegister={handleCourseRegister} variant="section" />

      {/* Why Choose CONFY Section */}
      <WhyChooseConfy />


      {/* Additional Profile Modal */}
      {selectedCourseId && (
        <AdditionalProfileModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          courseId={selectedCourseId}
          courseTitle={selectedCourseTitle}
          onComplete={handleProfileComplete}
        />
      )}

      {/* Auth Modal */}
      <SimpleAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultMode="signup"
      />
    </div>
  );
}
